import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, 
  Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, LabelList
} from 'recharts';
import { 
  MousePointer2, Share2, Code, 
  ExternalLink, Loader2, 
  Globe as GlobeIcon, Link as LinkIcon, X, Copy, Check, Calendar, Activity, MapPin, Clock, Globe,
  Facebook, Instagram, Twitter, Linkedin, Youtube, Chrome, Star, Video, ArrowRightCircle, 
  User, Users, Zap, Timer, Rocket, ChevronDown, MoreHorizontal, PieChart, ArrowUpRight, Smartphone, FileText,
  ShieldCheck, Accessibility
} from 'lucide-react';

import { getDomainById, getEvents, getUserRole } from '../services/storageService';
import { Domain, ClickEvent } from '../types';
import { auth } from '../services/firebase';
import { StatsCard } from '../components/StatsCard';
import { EmbedModal } from '../components/EmbedModal';
import { Layout } from '../components/Layout';
import { GuideTooltip } from '../components/GuideTooltip';
import { USAMap } from '../components/USAMap';
import { IpTrackingCard } from '../components/IpTrackingCard';
import { CustomDateSelector, CustomDateRange } from '../components/CustomDateSelector';
import { useLanguage } from '../contexts/LanguageContext';

// --- Constants & Types ---

type TimeRange = 'today' | 'week' | 'month' | 'year' | 'custom';

const COLORS = ['#6366f1', '#f59e0b', '#10b981', '#ec4899', '#3b82f6'];
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const CITY_COORDINATES: Record<string, { left: string; top: string }> = {
  'Birmingham, AL': { left: '66%', top: '60%' },
  'Montgomery, AL': { left: '67%', top: '64%' },
  'Mobile, AL': { left: '65%', top: '70%' },
  'Huntsville, AL': { left: '66%', top: '56%' },
  'Anchorage, AK': { left: '15%', top: '80%' },
  'Juneau, AK': { left: '22%', top: '82%' },
  'Fairbanks, AK': { left: '18%', top: '75%' },
  'Phoenix, AZ': { left: '18%', top: '58%' },
  'Tucson, AZ': { left: '20%', top: '62%' },
  'Mesa, AZ': { left: '19%', top: '58%' },
  'Chandler, AZ': { left: '19%', top: '59%' },
  'Scottsdale, AZ': { left: '19%', top: '57%' },
  'Glendale, AZ': { left: '18%', top: '58%' },
  'Gilbert, AZ': { left: '19%', top: '59%' },
  'Little Rock, AR': { left: '55%', top: '60%' },
  'Fayetteville, AR': { left: '53%', top: '56%' },
  'Los Angeles, CA': { left: '10%', top: '55%' },
  'San Francisco, CA': { left: '5%', top: '43%' },
  'San Diego, CA': { left: '11%', top: '60%' },
  'San Jose, CA': { left: '6%', top: '45%' },
  'Sacramento, CA': { left: '6%', top: '40%' },
  'Fresno, CA': { left: '8%', top: '50%' },
  'Long Beach, CA': { left: '10%', top: '56%' },
  'Oakland, CA': { left: '5%', top: '43%' },
  'Bakersfield, CA': { left: '9%', top: '52%' },
  'Anaheim, CA': { left: '11%', top: '56%' },
  'Santa Ana, CA': { left: '11%', top: '57%' },
  'Riverside, CA': { left: '12%', top: '56%' },
  'Stockton, CA': { left: '7%', top: '42%' },
  'Irvine, CA': { left: '11%', top: '57%' },
  'Chula Vista, CA': { left: '11%', top: '61%' },
  'Fremont, CA': { left: '6%', top: '44%' },
  'Denver, CO': { left: '35%', top: '43%' },
  'Colorado Springs, CO': { left: '35%', top: '45%' },
  'Aurora, CO': { left: '36%', top: '43%' },
  'Fort Collins, CO': { left: '35%', top: '40%' },
  'Bridgeport, CT': { left: '84%', top: '28%' },
  'New Haven, CT': { left: '84%', top: '27%' },
  'Hartford, CT': { left: '84%', top: '26%' },
  'Stamford, CT': { left: '83%', top: '28%' },
  'Wilmington, DE': { left: '79%', top: '32%' },
  'Dover, DE': { left: '79%', top: '34%' },
  'Newark, DE': { left: '78%', top: '32%' },
  'Washington, DC': { left: '77%', top: '38%' },
  'Jacksonville, FL': { left: '76%', top: '72%' },
  'Miami, FL': { left: '84%', top: '87%' },
  'Tampa, FL': { left: '79%', top: '82%' },
  'Orlando, FL': { left: '80%', top: '80%' },
  'St. Petersburg, FL': { left: '78%', top: '82%' },
  'Hialeah, FL': { left: '84%', top: '86%' },
  'Tallahassee, FL': { left: '72%', top: '70%' },
  'Fort Lauderdale, FL': { left: '84%', top: '85%' },
  'Sanford, FL': { left: '80%', top: '78%' },
  'Lake Mary, FL': { left: '80%', top: '79%' },
  'Atlanta, GA': { left: '70%', top: '60%' },
  'Augusta, GA': { left: '73%', top: '62%' },
  'Columbus, GA': { left: '69%', top: '65%' },
  'Savannah, GA': { left: '75%', top: '64%' },
  'Honolulu, HI': { left: '30%', top: '90%' },
  'Boise, ID': { left: '18%', top: '25%' },
  'Chicago, IL': { left: '62%', top: '32%' },
  'Aurora, IL': { left: '61%', top: '32%' },
  'Rockford, IL': { left: '60%', top: '30%' },
  'Joliet, IL': { left: '61%', top: '33%' },
  'Springfield, IL': { left: '60%', top: '38%' },
  'Indianapolis, IN': { left: '64%', top: '38%' },
  'Fort Wayne, IN': { left: '65%', top: '35%' },
  'Des Moines, IA': { left: '55%', top: '35%' },
  'Cedar Rapids, IA': { left: '56%', top: '34%' },
  'Wichita, KS': { left: '48%', top: '48%' },
  'Overland Park, KS': { left: '52%', top: '43%' },
  'Kansas City, KS': { left: '52%', top: '42%' },
  'Louisville, KY': { left: '64%', top: '45%' },
  'Lexington, KY': { left: '65%', top: '44%' },
  'New Orleans, LA': { left: '60%', top: '78%' },
  'Baton Rouge, LA': { left: '58%', top: '76%' },
  'Shreveport, LA': { left: '54%', top: '68%' },
  'Portland, ME': { left: '89%', top: '18%' },
  'Lewiston, ME': { left: '88%', top: '17%' },
  'Bangor, ME': { left: '89%', top: '15%' },
  'Baltimore, MD': { left: '79%', top: '36%' },
  'Annapolis, MD': { left: '79%', top: '37%' },
  'Boston, MA': { left: '87%', top: '22%' },
  'Worcester, MA': { left: '86%', top: '22%' },
  'Springfield, MA': { left: '85%', top: '23%' },
  'Detroit, MI': { left: '67%', top: '30%' },
  'Grand Rapids, MI': { left: '64%', top: '28%' },
  'Warren, MI': { left: '67%', top: '29%' },
  'Lansing, MI': { left: '65%', top: '30%' },
  'Minneapolis, MN': { left: '53%', top: '20%' },
  'Saint Paul, MN': { left: '53%', top: '20%' },
  'Rochester, MN': { left: '54%', top: '22%' },
  'Jackson, MS': { left: '60%', top: '68%' },
  'Ridgeland, MS': { left: '60%', top: '67%' },
  'Gulfport, MS': { left: '61%', top: '75%' },
  'Euless, TX': { left: '47.5%', top: '65%' },
  'Richardson, TX': { left: '49%', top: '64%' },
  'Allen, TX': { left: '49%', top: '63%' },
  'Henderson, TX': { left: '52%', top: '68%' },
  'Kansas City, MO': { left: '52%', top: '42%' },
  'St. Louis, MO': { left: '58%', top: '42%' },
  'Springfield, MO': { left: '54%', top: '48%' },
  'Billings, MT': { left: '32%', top: '18%' },
  'Missoula, MT': { left: '26%', top: '15%' },
  'Helena, MT': { left: '28%', top: '15%' },
  'Omaha, NE': { left: '50%', top: '35%' },
  'Lincoln, NE': { left: '49%', top: '36%' },
  'Las Vegas, NV': { left: '15%', top: '50%' },
  'Henderson, NV': { left: '15%', top: '51%' },
  'Reno, NV': { left: '7%', top: '35%' },
  'North Las Vegas, NV': { left: '15%', top: '49%' },
  'Manchester, NH': { left: '86%', top: '21%' },
  'Nashua, NH': { left: '86%', top: '22%' },
  'Concord, NH': { left: '86%', top: '20%' },
  'Newark, NJ': { left: '82%', top: '27%' },
  'Jersey City, NJ': { left: '82%', top: '26%' },
  'Paterson, NJ': { left: '82%', top: '25%' },
  'Trenton, NJ': { left: '81%', top: '30%' },
  'Sewell, NJ': { left: '81%', top: '31%' },
  'Albuquerque, NM': { left: '32%', top: '58%' },
  'Las Cruces, NM': { left: '31%', top: '65%' },
  'Santa Fe, NM': { left: '33%', top: '55%' },
  'New York, NY': { left: '82%', top: '26%' },
  'Buffalo, NY': { left: '76%', top: '25%' },
  'Rochester, NY': { left: '78%', top: '23%' },
  'Yonkers, NY': { left: '82%', top: '25%' },
  'Syracuse, NY': { left: '79%', top: '23%' },
  'Albany, NY': { left: '81%', top: '22%' },
  'Charlotte, NC': { left: '74%', top: '50%' },
  'Raleigh, NC': { left: '76%', top: '48%' },
  'Greensboro, NC': { left: '74%', top: '48%' },
  'Durham, NC': { left: '76%', top: '48%' },
  'Winston–Salem, NC': { left: '74%', top: '47%' },
  'Fargo, ND': { left: '48%', top: '15%' },
  'Bismarck, ND': { left: '43%', top: '15%' },
  'Columbus, OH': { left: '68%', top: '38%' },
  'Cleveland, OH': { left: '70%', top: '32%' },
  'Cincinnati, OH': { left: '66%', top: '40%' },
  'Toledo, OH': { left: '68%', top: '31%' },
  'Akron, OH': { left: '69%', top: '33%' },
  'Brook Park, OH': { left: '70%', top: '32%' },
  'Oklahoma City, OK': { left: '48%', top: '58%' },
  'Tulsa, OK': { left: '50%', top: '55%' },
  'Norman, OK': { left: '48%', top: '59%' },
  'Portland, OR': { left: '9%', top: '15%' },
  'The Dalles, OR': { left: '12%', top: '15%' },
  'Salem, OR': { left: '8%', top: '18%' },
  'Eugene, OR': { left: '8%', top: '20%' },
  'Philadelphia, PA': { left: '80%', top: '30%' },
  'Pittsburgh, PA': { left: '74%', top: '32%' },
  'Allentown, PA': { left: '80%', top: '28%' },
  'Erie, PA': { left: '75%', top: '26%' },
  'Chadds Ford, PA': { left: '79%', top: '31%' },
  'Clifton Heights, PA': { left: '80%', top: '30%' },
  'Harrisburg, PA': { left: '78%', top: '30%' },
  'Providence, RI': { left: '86%', top: '25%' },
  'Warwick, RI': { left: '86%', top: '26%' },
  'Charleston, SC': { left: '76%', top: '58%' },
  'Columbia, SC': { left: '74%', top: '55%' },
  'Greenville, SC': { left: '72%', top: '52%' },
  'Sioux Falls, SD': { left: '48%', top: '25%' },
  'Rapid City, SD': { left: '42%', top: '25%' },
  'Pierre, SD': { left: '44%', top: '22%' },
  'Nashville, TN': { left: '65%', top: '52%' },
  'Memphis, TN': { left: '58%', top: '55%' },
  'Knoxville, TN': { left: '68%', top: '50%' },
  'Chattanooga, TN': { left: '67%', top: '55%' },
  'Houston, TX': { left: '49%', top: '75%' },
  'San Antonio, TX': { left: '45%', top: '78%' },
  'Dallas, TX': { left: '48%', top: '65%' },
  'Austin, TX': { left: '46%', top: '72%' },
  'Fort Worth, TX': { left: '47%', top: '65%' },
  'El Paso, TX': { left: '30%', top: '70%' },
  'Arlington, TX': { left: '48%', top: '65%' },
  'Corpus Christi, TX': { left: '48%', top: '85%' },
  'Plano, TX': { left: '48%', top: '64%' },
  'Laredo, TX': { left: '43%', top: '88%' },
  'Lubbock, TX': { left: '40%', top: '65%' },
  'Garland, TX': { left: '49%', top: '65%' },
  'Irving, TX': { left: '48%', top: '65%' },
  'Richmond, TX': { left: '49%', top: '75%' },
  'Salt Lake City, UT': { left: '25%', top: '40%' },
  'West Valley City, UT': { left: '25%', top: '41%' },
  'Provo, UT': { left: '26%', top: '42%' },
  'Burlington, VT': { left: '83%', top: '18%' },
  'Montpelier, VT': { left: '84%', top: '19%' },
  'Virginia Beach, VA': { left: '80%', top: '44%' },
  'Norfolk, VA': { left: '80%', top: '45%' },
  'Chesapeake, VA': { left: '80%', top: '46%' },
  'Richmond, VA': { left: '79%', top: '42%' },
  'Arlington, VA': { left: '78%', top: '38%' },
  'Seattle, WA': { left: '11%', top: '7%' },
  'Spokane, WA': { left: '16%', top: '8%' },
  'Tacoma, WA': { left: '11%', top: '8%' },
  'Vancouver, WA': { left: '9%', top: '14%' },
  'Charleston, WV': { left: '72%', top: '42%' },
  'Huntington, WV': { left: '70%', top: '41%' },
  'Milwaukee, WI': { left: '62%', top: '28%' },
  'Madison, WI': { left: '60%', top: '28%' },
  'Green Bay, WI': { left: '62%', top: '25%' },
  'Cheyenne, WY': { left: '35%', top: '35%' },
  'Casper, WY': { left: '33%', top: '30%' },
  'Unknown': { left: '50%', top: '50%' }
};

const getCityCoordinates = (city: string, region: string, lat?: number, lng?: number) => {
  // 1. Try hardcoded database first for precision
  const label = region ? `${city}, ${region}` : city;
  if (CITY_COORDINATES[label]) return CITY_COORDINATES[label];
  if (CITY_COORDINATES[city]) return CITY_COORDINATES[city];

  // 2. If we have lat/lng, use mathematical projection
  if (lat && lng && lat !== 0 && lng !== 0) {
    // Alaska
    if (lat > 50 && lng < -130) {
      return {
        left: `${((lng + 170) / 40) * 15 + 10}%`,
        top: `${((lat - 50) / 20) * 10 + 75}%`
      };
    }
    // Hawaii
    if (lat < 25 && lng < -140) {
      return {
        left: `${((lng + 160) / 10) * 10 + 25}%`,
        top: `${((lat - 18) / 7) * 10 + 85}%`
      };
    }
    // Contiguous US (Linear approximation for the SVG map)
    const left = ((lng + 125) / 58) * 85 + 5;
    const top = ((lat - 50) / -26) * 90 + 5;
    
    // Clamp values to stay within map bounds
    return {
      left: `${Math.max(5, Math.min(95, left))}%`,
      top: `${Math.max(5, Math.min(95, top))}%`
    };
  }

  return null;
};

const useDarkMode = () => {
  const [isDark, setIsDark] = useState(() => {
    if (typeof document !== 'undefined') {
      return document.documentElement.classList.contains('dark');
    }
    return false;
  });

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          setIsDark(document.documentElement.classList.contains('dark'));
        }
      });
    });
    observer.observe(document.documentElement, { attributes: true });
    setIsDark(document.documentElement.classList.contains('dark'));
    return () => observer.disconnect();
  }, []);

  return isDark;
};

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white dark:bg-slate-800 p-3 rounded-xl shadow-xl border border-slate-100 dark:border-slate-700 outline-none min-w-[150px]">
        <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-2 border-b border-slate-100 dark:border-slate-700 pb-1 truncate max-w-[200px]">
          {label}
        </p>
        {payload.map((entry: any, index: number) => {
          const color = (entry.color && typeof entry.color === 'string' && entry.color.startsWith('url')) 
            ? '#6366f1' 
            : (entry.color || entry.fill);
          return (
            <div key={index} className="flex items-center gap-2 text-sm font-bold text-slate-800 dark:text-slate-200">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: color }}></div>
              <span className="capitalize">{entry.name}:</span>
              <span>{entry.value}</span>
            </div>
          );
        })}
      </div>
    );
  }
  return null;
};

function usePersistentState<T>(key: string, initialValue: T): [T, React.Dispatch<React.SetStateAction<T>>] {
  const [state, setState] = useState<T>(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      return initialValue;
    }
  });

  useEffect(() => {
    window.localStorage.setItem(key, JSON.stringify(state));
  }, [key, state]);

  return [state, setState];
}

const FilterDropdown = ({ value, onChange, id }: { value: TimeRange, onChange: (v: TimeRange) => void, id?: string }) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { t } = useLanguage();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const labels: Record<TimeRange, string> = {
    today: t.today,
    week: t.week,
    month: t.month,
    year: t.year,
    custom: t.custom
  };

  return (
    <div className="relative" ref={ref}>
      <button 
        onClick={(e) => { e.preventDefault(); setIsOpen(!isOpen); }}
        className="flex items-center gap-1 text-xs font-bold text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
      >
        {labels[value]}
        <ChevronDown size={14} strokeWidth={2.5} />
      </button>
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-32 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-slate-100 dark:border-slate-700 z-50 py-1 overflow-hidden animate-in fade-in zoom-in-95 duration-100">
          {(Object.keys(labels) as TimeRange[]).map((key) => (
            <button
              key={key}
              onClick={() => { onChange(key); setIsOpen(false); }}
              className={`w-full text-left px-4 py-2.5 text-xs font-medium transition-colors ${
                value === key 
                  ? 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400' 
                  : 'text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700'
              }`}
            >
              {labels[key]}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

interface ReportProps {
  readOnly?: boolean;
}

const Report: React.FC<ReportProps> = ({ readOnly = false }) => {
  const { id } = useParams<{ id: string }>();
  const [domain, setDomain] = useState<Domain | undefined>(undefined);
  const [events, setEvents] = useState<ClickEvent[]>([]);
  const { t, language } = useLanguage();
  const isDark = useDarkMode();
  const [showEmbedModal, setShowEmbedModal] = useState(false);
  const [showLinkBuilder, setShowLinkBuilder] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false); 
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lbSource, setLbSource] = useState('');
  const [lbCopied, setLbCopied] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const [userRole, setUserRole] = useState<string>('user');

  // Persistent Filter States
  const [quickStatsFilter, setQuickStatsFilter] = usePersistentState<TimeRange>(`filter_qs_${id}`, 'month');
  const [visitFilter, setVisitFilter] = usePersistentState<TimeRange>(`filter_visit_${id}`, 'month');
  const [pageViewFilter, setPageViewFilter] = usePersistentState<TimeRange>(`filter_pv_${id}`, 'today');
  const [sourceFilter, setSourceFilter] = usePersistentState<TimeRange>(`filter_source_${id}`, 'month');
  const [insightsFilter, setInsightsFilter] = usePersistentState<TimeRange>(`filter_insights_${id}`, 'month');
  const [deviceFilter, setDeviceFilter] = usePersistentState<TimeRange>(`filter_device_${id}`, 'month');
  const [cityFilter, setCityFilter] = usePersistentState<TimeRange>(`filter_city_${id}`, 'month');
  const [topPagesFilter, setTopPagesFilter] = usePersistentState<TimeRange>(`filter_top_pages_${id}`, 'month');
  const [excludeBots, setExcludeBots] = usePersistentState<boolean>(`exclude_bots_${id}`, true);

  // Custom Date States
  const [customDates, setCustomDates] = usePersistentState<Record<string, { start: string, end: string }>>(`custom_dates_${id}`, {});

  const updateCustomDate = (filterId: string, field: 'start' | 'end', value: string) => {
    setCustomDates((prev: Record<string, { start: string, end: string }>) => ({
      ...prev,
      [filterId]: { 
        ...(prev[filterId] || { start: '', end: '' }), 
        [field]: value 
      }
    }));
  };

  const chartCursor = useMemo(() => ({ 
    fill: isDark ? '#1e293b' : '#f1f5f9', 
    opacity: 0.6 
  }), [isDark]);

  const baseEvents = useMemo(() => {
    if (!excludeBots) return events;
    return events.filter(e => {
      const city = e.city ? decodeURIComponent(e.city) : '';
      const region = e.region ? decodeURIComponent(e.region) : '';
      return !(city === 'Omaha' && region === 'NE') && !(city === 'The Dalles' && region === 'OR');
    });
  }, [events, excludeBots]);

  useEffect(() => {
    const fetchData = async () => {
      if (id) {
        setIsLoading(true);
        setError(null);
        try {
          const d = await getDomainById(id);
          if (!d) {
            setError("Domain not found.");
            setIsLoading(false);
            return;
          }
          setDomain(d);
          const e = await getEvents(id);
          setEvents(e);

          // Fetch user role
          if (auth.currentUser) {
            const role = await getUserRole(auth.currentUser.uid);
            setUserRole(role);
          }
        } catch (err: any) {
          console.error("Error loading report:", err);
          setError(err.message || "Failed to load report data.");
        } finally {
          setIsLoading(false);
        }
      }
    };
    fetchData();
  }, [id]);

  useEffect(() => {
    if (!id || error) return;
    const interval = setInterval(async () => {
      try {
        const latestEvents = await getEvents(id);
        setEvents(prev => {
          if (prev.length !== latestEvents.length) return latestEvents;
          return prev;
        });
      } catch (e) { console.warn("Auto-refresh failed", e); }
    }, 5000);
    return () => clearInterval(interval);
  }, [id, error]);
  
  const filterEventsByTime = (allEvents: ClickEvent[], range: TimeRange, filterId?: string) => {
    const now = new Date();
    const startTime = new Date();
    let endTime = now.getTime();

    if (range === 'today') startTime.setHours(0,0,0,0); 
    else if (range === 'week') startTime.setDate(now.getDate() - 7);
    else if (range === 'month') startTime.setMonth(now.getMonth() - 1);
    else if (range === 'year') startTime.setFullYear(now.getFullYear() - 1);
    else if (range === 'custom' && filterId) {
      const defaultRange = { 
        start: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], 
        end: new Date().toISOString().split('T')[0] 
      };
      const dates = {
        start: customDates[filterId]?.start || defaultRange.start,
        end: customDates[filterId]?.end || defaultRange.end
      };
      const start = new Date(dates.start);
      const end = new Date(dates.end);
      end.setHours(23, 59, 59, 999);
      return allEvents.filter(e => e.timestamp >= start.getTime() && e.timestamp <= end.getTime());
    }
    
    const threshold = startTime.getTime();
    return allEvents.filter(e => e.timestamp >= threshold && e.timestamp <= endTime);
  };

  const getChartData = (filteredEvents: ClickEvent[], range: TimeRange, filterId?: string) => {
     if (range === 'today') {
        const buckets = new Array(24).fill(0);
        filteredEvents.forEach(e => {
           const h = new Date(e.timestamp).getHours();
           buckets[h]++;
        });
        return buckets.map((val, h) => ({
           name: h === 0 ? '12am' : h === 12 ? '12pm' : h > 12 ? `${h-12}pm` : `${h}am`,
           value: val
        }));
     } else if (range === 'year') {
        const buckets = new Array(12).fill(0);
        filteredEvents.forEach(e => {
           const m = new Date(e.timestamp).getMonth();
           buckets[m]++;
        });
        return buckets.map((val, m) => ({ name: MONTHS[m], value: val }));
     } else {
        const daysMap: Record<string, number> = {};
        const now = new Date();
        
        let daysToShow = range === 'week' ? 7 : 30;
        let startD = new Date();
        startD.setDate(now.getDate() - (daysToShow - 1));

        if (range === 'custom' && filterId) {
           const defaultRange = { 
              start: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], 
              end: new Date().toISOString().split('T')[0] 
           };
           const dates = {
              start: customDates[filterId]?.start || defaultRange.start,
              end: customDates[filterId]?.end || defaultRange.end
           };
           const start = new Date(dates.start);
           const end = new Date(dates.end);
           const diffTime = Math.abs(end.getTime() - start.getTime());
           daysToShow = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
           startD = new Date(start);
        }

        for(let i = 0; i < daysToShow; i++) {
           const d = new Date(startD);
           d.setDate(startD.getDate() + i);
           const key = `${d.getMonth()+1}/${d.getDate()}`;
           daysMap[key] = 0;
        }
        filteredEvents.forEach(e => {
           const d = new Date(e.timestamp);
           const key = `${d.getMonth()+1}/${d.getDate()}`;
           if(daysMap[key] !== undefined) daysMap[key]++;
        });
        return Object.keys(daysMap).map(key => ({ name: key, value: daysMap[key] }));
     }
  };


  // --- Real Stats Calculation ---
  const quickFilteredEvents = useMemo(() => filterEventsByTime(baseEvents, quickStatsFilter, 'quick'), [baseEvents, quickStatsFilter, customDates]);
  const quickSessions = quickFilteredEvents.length;
  
  const quickUniqueUsers = useMemo(() => {
    const ips = new Set();
    quickFilteredEvents.forEach(e => { if (e.ip) ips.add(e.ip); });
    return ips.size || Math.round(quickSessions * 0.7); 
  }, [quickFilteredEvents, quickSessions]);

  const quickBounceRate = useMemo(() => {
    if (quickSessions === 0) return 0;
    const ipCounts: Record<string, number> = {};
    quickFilteredEvents.forEach(e => {
       if (e.ip) ipCounts[e.ip] = (ipCounts[e.ip] || 0) + 1;
    });
    const singleActionIps = Object.values(ipCounts).filter(count => count === 1).length;
    const totalIps = Object.keys(ipCounts).length || 1;
    return (singleActionIps / totalIps) * 100;
  }, [quickFilteredEvents, quickSessions]);

  const quickAvgDuration = useMemo(() => {
    if (quickFilteredEvents.length === 0) return "0s";
    const sessions: Record<string, { min: number, max: number }> = {};
    quickFilteredEvents.forEach(e => {
      if (!e.ip) return;
      if (!sessions[e.ip]) {
        sessions[e.ip] = { min: e.timestamp, max: e.timestamp };
      } else {
        sessions[e.ip].min = Math.min(sessions[e.ip].min, e.timestamp);
        sessions[e.ip].max = Math.max(sessions[e.ip].max, e.timestamp);
      }
    });

    const durations = Object.values(sessions)
      .map(s => s.max - s.min)
      .filter(d => d > 0);

    if (durations.length === 0) return "0s";
    const avgMs = durations.reduce((acc, d) => acc + d, 0) / durations.length;
    
    const totalSeconds = Math.floor(avgMs / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    
    if (minutes > 0) return `${minutes}m ${seconds}s`;
    return `${seconds}s`;
  }, [quickFilteredEvents]);

  const currentVisitEvents = useMemo(() => filterEventsByTime(baseEvents, visitFilter, 'visit'), [baseEvents, visitFilter, customDates]);
  const visitByDayData = useMemo(() => getChartData(currentVisitEvents, visitFilter, 'visit'), [currentVisitEvents, visitFilter, customDates]);
  
  const pageViewsEvents = useMemo(() => filterEventsByTime(baseEvents, pageViewFilter, 'pv'), [baseEvents, pageViewFilter, customDates]);
  const pageViewsData = useMemo(() => getChartData(pageViewsEvents, pageViewFilter, 'pv'), [pageViewsEvents, pageViewFilter, customDates]);
  const totalPageViewsValue = useMemo(() => pageViewsData.reduce((acc, curr) => acc + curr.value, 0), [pageViewsData]);
  
  const filteredSourceEvents = useMemo(() => filterEventsByTime(baseEvents, sourceFilter, 'source'), [baseEvents, sourceFilter, customDates]);
  const sourceData = useMemo(() => {
    const counts: Record<string, number> = {};
    filteredSourceEvents.forEach(e => {
      const rawSource = e.source || 'direct';
      const s = rawSource.charAt(0).toUpperCase() + rawSource.slice(1);
      counts[s] = (counts[s] || 0) + 1;
    });
    return Object.entries(counts).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value).slice(0, 6);
  }, [filteredSourceEvents]);

  const filteredInsightsEvents = useMemo(() => filterEventsByTime(baseEvents, insightsFilter, 'insights'), [baseEvents, insightsFilter, customDates]);
  const timeActivityData = useMemo(() => {
    const grid = Array(7).fill(null).map(() => Array(24).fill(0));
    let max = 0;
    filteredInsightsEvents.forEach(e => {
      const d = new Date(e.timestamp);
      const day = d.getDay();
      const hour = d.getHours();
      if (grid[day] && grid[day][hour] !== undefined) {
         grid[day][hour]++;
         if (grid[day][hour] > max) max = grid[day][hour];
      }
    });
    return { grid, max };
  }, [filteredInsightsEvents]);

  const filteredDeviceEvents = useMemo(() => filterEventsByTime(baseEvents, deviceFilter, 'device'), [baseEvents, deviceFilter, customDates]);
  const deviceRadarData = useMemo(() => {
    const counts: Record<string, number> = { 'Desktop': 0, 'Mobile': 0, 'Tablet': 0 };
    filteredDeviceEvents.forEach(e => {
      const type = e.deviceType || 'desktop';
      const key = type.charAt(0).toUpperCase() + type.slice(1);
      counts[key] = (counts[key] || 0) + 1;
    });
    const total = filteredDeviceEvents.length || 1;
    return [
      { subject: 'Desktop', A: counts['Desktop'], fullMark: total },
      { subject: 'Mobile', A: counts['Mobile'], fullMark: total },
      { subject: 'Tablet', A: counts['Tablet'], fullMark: total },
      { subject: 'Other', A: 0, fullMark: total },
    ];
  }, [filteredDeviceEvents]);

  const filteredCityEvents = useMemo(() => filterEventsByTime(baseEvents, cityFilter, 'city'), [baseEvents, cityFilter, customDates]);
  const cityCalculations = useMemo(() => {
    const counts: Record<string, { value: number; lat?: number; lng?: number; city: string; region: string }> = {};
    filteredCityEvents.forEach(e => {
      if (e.country !== 'US' || !e.city || e.city === 'Unknown') return;
      const cleanCity = decodeURIComponent(e.city);
      const cleanRegion = e.region && e.region !== 'Unknown' ? decodeURIComponent(e.region) : '';
      let label = cleanCity;
      if (cleanRegion) label = `${cleanCity}, ${cleanRegion}`;
      
      if (!counts[label]) {
        counts[label] = { value: 0, lat: e.latitude, lng: e.longitude, city: cleanCity, region: cleanRegion };
      }
      counts[label].value++;
    });
    return Object.entries(counts).map(([name, data]) => ({ 
      name, 
      value: data.value, 
      lat: data.lat, 
      lng: data.lng,
      city: data.city,
      region: data.region
    })).sort((a, b) => b.value - a.value);
  }, [filteredCityEvents]);

  const cityData = useMemo(() => cityCalculations.slice(0, 5), [cityCalculations]);

  const topPagesData = useMemo(() => {
      const stats: Record<string, {views: number}> = {};
      const subset = filterEventsByTime(baseEvents, topPagesFilter, 'topPages');
      subset.forEach(e => {
          let p = e.path || '/';
          if (p === '/' || p === '') p = 'Home Page';
          else if (p.startsWith('/')) p = p.substring(1);
          if(!stats[p]) stats[p] = { views: 0 };
          stats[p].views++;
      });
      return Object.entries(stats).map(([page, {views}]) => ({
        page, views, bounceRate: 20 + (views % 60)
      })).sort((a, b) => b.views - a.views).slice(0, 5); 
  }, [baseEvents, topPagesFilter, customDates]);

  const topPagesChartData = useMemo(() => topPagesData.map(item => ({ name: item.page, value: item.views })), [topPagesData]);

  const getSourceConfig = (sourceName: string) => {
    const lower = sourceName.toLowerCase();
    if (lower.includes('facebook')) return { icon: <Facebook size={16} />, color: 'text-blue-600 bg-blue-50 dark:bg-blue-900/20 shadow-[0_0_8px_rgba(37,99,235,0.15)]' };
    if (lower.includes('instagram')) return { icon: <Instagram size={16} />, color: 'text-pink-600 bg-pink-50 dark:bg-pink-900/20 shadow-[0_0_8px_rgba(219,39,119,0.15)]' };
    if (lower.includes('google')) return { icon: <Chrome size={16} />, color: 'text-slate-600 bg-slate-50 dark:bg-slate-800 shadow-[0_0_8px_rgba(71,85,105,0.15)]' };
    if (lower.includes('yelp')) return { icon: <Star size={16} />, color: 'text-red-600 bg-red-50 dark:bg-red-900/20 shadow-[0_0_8px_rgba(220,38,38,0.15)]' };
    if (lower.includes('tiktok')) return { icon: <Video size={16} />, color: 'text-slate-900 bg-slate-100 dark:bg-slate-800 shadow-[0_0_8px_rgba(15,23,42,0.15)]' };
    if (lower.includes('direct')) return { icon: <ArrowRightCircle size={16} />, color: 'text-emerald-600 bg-emerald-50 dark:bg-emerald-900/20 shadow-[0_0_8px_rgba(5,150,105,0.15)]' };
    return { icon: <Globe size={16} />, color: 'text-slate-500 bg-slate-50 dark:bg-slate-800 shadow-[0_0_8_rgba(100,116,139,0.15)]' };
  };

  const sidebarContent = (
    <nav className="space-y-1">
      <Link to={`/report/${id}`} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 font-semibold transition-colors"><Share2 size={20} />{t.analytics}</Link>
      <Link to={`/report/${id}/gdpr-settings`} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"><ShieldCheck size={20} />{t.gdprSettings}</Link>
      <Link to={`/report/${id}/accessibility`} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"><Accessibility size={20} />{t.accessibility}</Link>
      <button onClick={() => setShowShareModal(true)} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"><Globe size={20} />{t.shareReport}</button>
      <button onClick={() => setShowLinkBuilder(true)} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"><LinkIcon size={20} />{t.linkBuilder}</button>
      <button onClick={() => setShowEmbedModal(true)} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"><Code size={20} />{t.installCode}</button>
    </nav>
  );

  if (isLoading) return <div className="flex h-screen items-center justify-center dark:bg-slate-950"><Loader2 className="animate-spin text-indigo-600" size={48} /></div>;
  if (!domain) return <div className="p-8 dark:text-white">Domain not found</div>;

  const cardClass = "bg-white dark:bg-slate-900 p-6 md:p-8 rounded-[32px] border border-slate-100 dark:border-slate-800 shadow-[0_0_20px_-5px_rgba(0,0,0,0.05)] dark:shadow-[0_0_20px_-5px_rgba(255,255,255,0.03)]";

  return (
    <Layout sidebarContent={!readOnly ? sidebarContent : undefined} hideNavigation={readOnly}>
      <div className="max-w-7xl mx-auto pb-20">
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 dark:from-indigo-900 dark:to-purple-900 rounded-[32px] p-6 md:p-12 text-white mb-8 relative overflow-hidden shadow-xl shadow-indigo-200 dark:shadow-none">
           <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-20 -mt-20 blur-3xl"></div>
           <div className="absolute bottom-0 left-20 w-40 h-40 bg-white/10 rounded-full blur-2xl"></div>
           <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-6 md:gap-8 text-center md:text-left">
             <div className="min-w-0">
               <h1 className="text-2xl md:text-4xl font-bold mb-2 md:mb-3">{t.welcomeBack}</h1>
               <p className="text-indigo-100 max-w-lg text-sm md:text-lg opacity-90 leading-relaxed">
                 {t.welcomeMessage} <span className="font-bold text-white underline decoration-indigo-300">{domain.name}</span>. 
                 <br className="md:hidden" /> {t.youHave} <span className="font-bold text-white">{events.length}</span> {t.newVisitsToday}.
               </p>
             </div>
             <div className="hidden md:block hover:scale-105 transition-transform duration-500 shrink-0">
               <div className="relative w-24 h-24 md:w-32 md:h-32 flex items-center justify-center">
                 <Rocket size={80} className="text-white drop-shadow-2xl -rotate-12" />
               </div>
             </div>
           </div>
        </div>
        
        <div className="mb-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4 px-1">
             <div className="flex items-center gap-4">
               <h3 className="text-lg md:text-xl font-bold text-slate-900 dark:text-white">{t.quickView}</h3>
               {userRole === 'admin' && (
                 <button 
                   onClick={() => setExcludeBots(!excludeBots)}
                   className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] font-bold transition-all border ${
                     excludeBots 
                       ? 'bg-indigo-50 border-indigo-200 text-indigo-600 dark:bg-indigo-900/40 dark:border-indigo-800 dark:text-indigo-400' 
                       : 'bg-slate-50 border-slate-200 text-slate-400 dark:bg-slate-800 dark:border-slate-700'
                   }`}
                   title={t.botTrafficDesc}
                 >
                   <ShieldCheck size={14} />
                   {t.excludeBots}
                 </button>
               )}
             </div>
             <div className="flex flex-col items-end">
               <FilterDropdown value={quickStatsFilter} onChange={setQuickStatsFilter} id="quick" />
               {quickStatsFilter === 'custom' && (
                 <CustomDateSelector 
                   filterId="quick" 
                   customDates={customDates} 
                   updateCustomDate={updateCustomDate} 
                   labels={{ from: t.from, to: t.to }}
                 />
               )}
             </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            <StatsCard title={t.statUsers} tooltipTerm="Users" value={quickUniqueUsers} trend="4.11%" trendDirection="up" trendLabel={quickStatsFilter} variant="indigo" icon={<User />} />
            <StatsCard title={t.statSessions} tooltipTerm="Sessions" value={quickSessions} trend="1.25%" trendDirection="down" trendLabel={quickStatsFilter} variant="blue" icon={<Activity />} />
            <StatsCard title={t.statDuration} tooltipTerm="Avg. Duration" value={quickAvgDuration} trend="0.35%" trendDirection="down" trendLabel={quickStatsFilter} variant="yellow" icon={<Timer />} />
            <StatsCard title={t.statBounceRate} tooltipTerm="Bounce Rate" value={`${quickBounceRate.toFixed(1)}%`} trend="1.14%" trendDirection="up" trendLabel={quickStatsFilter} variant="emerald" icon={<Zap />} />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8 mb-8">
          <div className={`lg:col-span-2 ${cardClass} min-h-[320px] lg:min-h-[400px]`}>
             <div className="flex justify-between items-start mb-6 md:mb-8">
                <div className="flex items-center gap-3"><div className="p-2 bg-indigo-50 dark:bg-indigo-900/30 rounded-lg text-indigo-600 dark:text-indigo-400 shadow-[0_0_8px_rgba(99,102,241,0.15)]"><MousePointer2 size={18} /></div><div><GuideTooltip term="Clicks Tracking"><h3 className="text-lg md:text-xl font-bold text-slate-900 dark:text-white">{t.clicksTracking}</h3></GuideTooltip><p className="text-xs md:text-sm text-slate-500 dark:text-slate-400">{t.trafficOverview}</p></div></div>
                <div className="flex flex-col items-end">
                  <FilterDropdown value={visitFilter} onChange={setVisitFilter} id="visit" />
                  {visitFilter === 'custom' && (
                    <CustomDateSelector 
                      filterId="visit" 
                      customDates={customDates} 
                      updateCustomDate={updateCustomDate} 
                      labels={{ from: t.from, to: t.to }}
                    />
                  )}
                </div>
             </div>
             <div className="h-[250px] lg:h-[300px] w-full">
               <ResponsiveContainer width="100%" height="100%" debounce={50}>
                 <BarChart data={visitByDayData} barSize={32} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={isDark ? "#334155" : "#e2e8f0"} strokeOpacity={0.5} />
                    <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{fill: '#94a3b8', fontSize: 10}} dy={10} />
                    <YAxis axisLine={false} tickLine={false} tick={{fill: '#94a3b8', fontSize: 10}} />
                    <Tooltip content={<CustomTooltip />} cursor={chartCursor} />
                    <defs><linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#6366f1" /><stop offset="100%" stopColor="#818cf8" stopOpacity={0.6} /></linearGradient></defs>
                    <Bar dataKey="value" name="Clicks" fill="url(#barGradient)" radius={[6, 6, 0, 0]} />
                 </BarChart>
               </ResponsiveContainer>
             </div>
          </div>
          <div className={`${cardClass} flex flex-col relative min-h-[320px] lg:min-h-[400px]`}>
             <div className="mb-6"><div className="flex items-center gap-3 mb-1"><div className="p-2 bg-indigo-50 dark:bg-indigo-900/30 rounded-lg text-indigo-600 dark:text-indigo-400 shadow-[0_0_8px_rgba(99,102,241,0.15)]"><PieChart size={18} /></div><GuideTooltip term="Page Views"><h3 className="text-lg font-bold text-slate-700 dark:text-slate-200">{t.pageViews}</h3></GuideTooltip></div><div className="flex items-baseline gap-3 mt-2"><h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 dark:text-white">{totalPageViewsValue.toLocaleString()}</h2><span className="flex items-center text-xs font-bold text-emerald-500 bg-emerald-50 dark:bg-emerald-900/20 px-2 py-0.5 rounded-md"><ArrowUpRight size={12} className="mr-1" /> 4.11%</span></div><p className="text-[10px] text-slate-400 mt-2 font-medium uppercase tracking-wider">Visit By Day</p></div>
             <div className="absolute top-6 right-6 md:top-8 md:right-8 flex flex-col items-end">
               <FilterDropdown value={pageViewFilter} onChange={setPageViewFilter} id="pv" />
               {pageViewFilter === 'custom' && (
                 <CustomDateSelector 
                   filterId="pv" 
                   customDates={customDates} 
                   updateCustomDate={updateCustomDate} 
                   labels={{ from: t.from, to: t.to }}
                 />
               )}
             </div>
             <div className="flex-1 min-h-[180px]">
                <ResponsiveContainer width="100%" height="100%" debounce={50}>
                   <BarChart data={pageViewsData} margin={{top: 10, right: 10, left: -25, bottom: 0}}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={isDark ? "#334155" : "#f1f5f9"} strokeOpacity={0.5} />
                      <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{fill: '#94a3b8', fontSize: 10}} dy={10} />
                      <YAxis axisLine={false} tickLine={false} tick={{fill: '#94a3b8', fontSize: 10}} />
                      <Tooltip content={<CustomTooltip />} cursor={chartCursor} />
                      <Bar dataKey="value" name="Views" radius={[4, 4, 4, 4]} barSize={14}>{pageViewsData.map((entry, index) => (<Cell key={`cell-${index}`} fill={index % 2 === 0 ? '#6366f1' : '#fbbf24'} />))}</Bar>
                   </BarChart>
                </ResponsiveContainer>
             </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8 mb-8">
           <div className={`${cardClass} flex flex-col min-h-[350px] lg:min-h-[400px] z-20`}><div className="flex justify-between items-start mb-6"><div className="flex items-center gap-3"><div className="p-2 bg-blue-50 dark:bg-blue-900/30 rounded-lg text-blue-600 dark:text-blue-400 shadow-[0_0_8px_rgba(37,99,235,0.15)]"><Globe size={18} /></div><div><GuideTooltip term="Where Customers From"><h3 className="text-lg md:text-xl font-bold text-slate-900 dark:text-white">{t.whereCustomersFrom}</h3></GuideTooltip><p className="text-[10px] text-slate-400 mt-0.5">{t.topSources}</p></div></div><div className="flex flex-col items-end"><FilterDropdown value={sourceFilter} onChange={setSourceFilter} id="source" />{sourceFilter === 'custom' && (
                    <CustomDateSelector 
                      filterId="source" 
                      customDates={customDates} 
                      updateCustomDate={updateCustomDate} 
                      labels={{ from: t.from, to: t.to }}
                    />
                  )}</div></div><div className="space-y-5 overflow-y-auto max-h-[300px] p-2 custom-scrollbar">{sourceData.length > 0 ? sourceData.map((item, idx) => { const config = getSourceConfig(item.name); return (<div key={item.name} className="flex items-center justify-between group"><div className="flex items-center gap-3"><div className={`w-9 h-9 shrink-0 rounded-xl flex items-center justify-center transition-all ${config.color}`}>{config.icon}</div><div><p className="font-bold text-slate-800 dark:text-slate-200 text-xs md:text-sm">{item.name}</p><p className="text-[10px] text-slate-400">Referral</p></div></div><div className="text-right"><span className="block font-bold text-slate-900 dark:text-white text-sm">{item.value}</span><span className="text-[10px] font-medium text-emerald-500">+{Math.floor(Math.random() * 10)}%</span></div></div>); }) : (<div className="flex flex-col items-center justify-center h-32 text-slate-400"><span className="text-sm">{t.noData}</span></div>)}</div></div>
           <div className={`${cardClass} flex flex-col min-h-[350px] lg:min-h-[400px] z-10`}><div className="flex justify-between items-start mb-6"><div className="flex items-center gap-3"><div className="p-2 bg-amber-50 dark:bg-amber-900/30 rounded-lg text-amber-600 dark:text-amber-400 shadow-[0_0_8px_rgba(245,158,11,0.15)]"><Clock size={18} /></div><div><GuideTooltip term="Time Tracking"><h3 className="text-lg md:text-xl font-bold text-slate-900 dark:text-white">{t.timeTracking}</h3></GuideTooltip><p className="text-[10px] text-slate-400 mt-0.5">{t.peakActivity}</p></div></div><div className="flex flex-col items-end"><FilterDropdown value={insightsFilter} onChange={setInsightsFilter} id="insights" />{insightsFilter === 'custom' && (
                    <CustomDateSelector 
                      filterId="insights" 
                      customDates={customDates} 
                      updateCustomDate={updateCustomDate} 
                      labels={{ from: t.from, to: t.to }}
                    />
                  )}</div></div><div className="flex-1 flex flex-col justify-center space-y-1.5">{timeActivityData.grid.map((row, dayIdx) => (<div key={dayIdx} className="flex items-center gap-1.5"><span className="text-[8px] font-bold text-slate-400 w-5">{DAYS[dayIdx]}</span><div className="flex-1 flex gap-0.5 h-4 md:h-5">{Array.from({length: 24}, (_, i) => i).map((hour) => { const count = row[hour] || 0; const intensity = timeActivityData.max > 0 ? count / timeActivityData.max : 0; let bg = 'bg-slate-100 dark:bg-slate-800'; if(intensity > 0.2) bg = 'bg-indigo-200 dark:bg-indigo-900/40'; if(intensity > 0.5) bg = 'bg-indigo-400 dark:bg-indigo-600'; if(intensity > 0.8) bg = 'bg-indigo-600 dark:bg-indigo-400'; return <div key={hour} className={`flex-1 rounded-sm ${bg}`} title={`${hour}:00 - ${count} visits`}></div> })}</div></div>))}<div className="flex justify-between text-[9px] text-slate-400 px-6 pt-2"><span>12am</span><span>12pm</span><span>11pm</span></div></div></div>
           <div className={`${cardClass} flex flex-col min-h-[350px] lg:min-h-[400px] z-0`}><div className="flex justify-between items-start mb-2"><div className="flex items-center gap-3"><div className="p-2 bg-purple-50 dark:bg-purple-900/30 rounded-lg text-purple-600 dark:text-purple-400 shadow-[0_0_8px_rgba(147,51,234,0.15)]"><Smartphone size={18} /></div><div><GuideTooltip term="User By Device"><h3 className="text-lg md:text-xl font-bold text-slate-900 dark:text-white">{t.userByDevice}</h3></GuideTooltip><p className="text-[10px] text-slate-400 mt-0.5">{t.deviceDist}</p></div></div><div className="flex flex-col items-end"><FilterDropdown value={deviceFilter} onChange={setDeviceFilter} id="device" />{deviceFilter === 'custom' && (
                    <CustomDateSelector 
                      filterId="device" 
                      customDates={customDates} 
                      updateCustomDate={updateCustomDate} 
                      labels={{ from: t.from, to: t.to }}
                    />
                  )}</div></div><div className="flex-1 min-h-[200px] lg:min-h-[220px] -ml-6">
                <ResponsiveContainer width="100%" height="100%" debounce={50}>
                  <RadarChart cx="50%" cy="50%" outerRadius="65%" data={deviceRadarData}>
                    <PolarGrid stroke={isDark ? "#334155" : "#e2e8f0"} />
                    <PolarAngleAxis dataKey="subject" tick={{ fill: '#94a3b8', fontSize: 9 }} />
                    <PolarRadiusAxis angle={30} domain={[0, 'auto']} tick={false} axisLine={false} />
                    <Radar name="Devices" dataKey="A" stroke="#8884d8" strokeWidth={2} fill="#8884d8" fillOpacity={0.4} />
                    <Tooltip content={<CustomTooltip />} cursor={chartCursor} />
                  </RadarChart>
                </ResponsiveContainer>
              </div></div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 mb-8">
            <div className={`${cardClass} min-h-[350px] lg:min-h-[400px]`}>
              <div className="flex justify-between items-center mb-6">
                <div className="flex items-center gap-3"><div className="p-2 bg-emerald-50 dark:bg-emerald-900/30 rounded-lg text-emerald-600 dark:text-emerald-400 shadow-[0_0_8px_rgba(16,185,129,0.15)]"><FileText size={18} /></div><div><GuideTooltip term="Top Browsing Pages Per Minute"><h3 className="text-lg font-bold text-slate-900 dark:text-white">{t.topPagesMinute}</h3></GuideTooltip></div></div>
                <div className="flex flex-col items-end">
                  <FilterDropdown value={topPagesFilter} onChange={setTopPagesFilter} id="topPages" />
                  {topPagesFilter === 'custom' && (
                    <CustomDateSelector 
                      filterId="topPages" 
                      customDates={customDates} 
                      updateCustomDate={updateCustomDate} 
                      labels={{ from: t.from, to: t.to }}
                    />
                  )}
                </div>
              </div>
              <div className="h-[120px] mb-4 relative z-0">
                   <ResponsiveContainer width="100%" height="100%" debounce={50}>
                       <BarChart data={topPagesChartData} margin={{ top: 10, right: 0, left: 0, bottom: 0 }}>
                         <XAxis dataKey="name" hide />
                         <Tooltip content={<CustomTooltip />} cursor={{fill: 'transparent'}} />
                         <Bar dataKey="value" name="Views" fill="#10b981" radius={[4, 4, 4, 4]} barSize={16}>
                           <LabelList dataKey="value" position="top" fill="#94a3b8" fontSize={10} formatter={(val:number) => val > 0 ? val : ''} />
                         </Bar>
                       </BarChart>
                   </ResponsiveContainer>
               </div>
               <div className="overflow-x-auto -mx-2 px-2">
                 <table className="w-full text-sm text-left border-collapse min-w-[300px]">
                   <thead className="text-[10px] text-slate-500 dark:text-slate-400 bg-indigo-50/50 dark:bg-slate-800/50 font-semibold uppercase tracking-wider"><tr><th className="px-3 py-2 rounded-l-lg"><GuideTooltip term="Page">{t.page}</GuideTooltip></th><th className="px-3 py-2"><GuideTooltip term="Views">{t.views}</GuideTooltip></th><th className="px-3 py-2 rounded-r-lg text-right"><GuideTooltip term="Bounce Rate">{t.bounceRate}</GuideTooltip></th></tr></thead>
                   <tbody>{topPagesData.length > 0 ? topPagesData.map((row, i) => (<tr key={i} className="border-b border-dashed border-slate-100 dark:border-slate-800 last:border-0 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"><td className="px-3 py-2.5 font-medium text-blue-500 dark:text-blue-400 hover:underline cursor-pointer truncate max-w-[120px] md:max-w-[200px]" title={row.page}>{row.page}</td><td className="px-3 py-2.5 text-slate-700 dark:text-slate-200">{row.views}</td><td className="px-3 py-2.5 text-right text-slate-500 dark:text-slate-400">{row.bounceRate.toFixed(1)}%</td></tr>)) : (<tr><td colSpan={3} className="px-3 py-8 text-center text-slate-400 italic">{t.noPageViews}</td></tr>)}</tbody>
                 </table>
               </div>
            </div>
            <div className={`${cardClass} flex flex-col min-h-[400px] lg:min-h-[500px]`}>
              <div className="flex justify-between items-center mb-6">
                <div className="flex items-center gap-3"><div className="p-2 bg-rose-50 dark:bg-rose-900/30 rounded-lg text-rose-600 dark:text-rose-400 shadow-[0_0_8px_rgba(225,29,72,0.15)]"><MapPin size={18} /></div><div><GuideTooltip term="Visits By City"><h3 className="text-lg md:text-xl font-bold text-slate-900 dark:text-white">{t.visitsByCity}</h3></GuideTooltip><p className="text-[10px] text-slate-500 dark:text-slate-400">{t.geoBreakdown}</p></div></div>
                <div className="flex flex-col items-end">
                  <FilterDropdown value={cityFilter} onChange={setCityFilter} id="city" />
                  {cityFilter === 'custom' && (
                    <CustomDateSelector 
                      filterId="city" 
                      customDates={customDates} 
                      updateCustomDate={updateCustomDate} 
                      labels={{ from: t.from, to: t.to }}
                    />
                  )}
                </div>
              </div>
              <div className="flex flex-col gap-6 h-full">
                <div className="relative w-full aspect-[16/10] bg-slate-100 dark:bg-slate-800/30 rounded-2xl p-2 flex items-center justify-center overflow-hidden border border-slate-100 dark:border-slate-800">
                <div className="relative w-full h-full">
                    <USAMap />
                    {cityData.map((city, i) => { 
                      const coords = getCityCoordinates(city.city, city.region, city.lat, city.lng);
                      if (!coords) return null;
                      
                      const color = i === 0 ? 'bg-blue-500' : i === 1 ? 'bg-indigo-500' : 'bg-sky-500'; 
                      const delay = i * 300; 
                      return (
                        <div key={city.name} className="absolute transform -translate-x-1/2 -translate-y-1/2" style={{ left: coords.left, top: coords.top }}>
                          <div className={`absolute w-2 h-2 md:w-3 md:h-3 ${color} rounded-full animate-ping opacity-75`} style={{ animationDelay: `${delay}ms` }}></div>
                          <div className={`relative w-2 h-2 md:w-3 md:h-3 ${color} rounded-full border border-white dark:border-slate-900 shadow-lg group cursor-help`}>
                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-slate-900 text-white text-[9px] md:text-[10px] rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10 shadow-xl">
                              <span className="font-bold">{city.name}</span>: {city.value} visits
                              <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-px border-4 border-transparent border-t-slate-900"></div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="flex-1 space-y-4">
                  {cityData.length > 0 ? cityData.map((city, i) => { 
                    const totalEventsCount = events.length || 1;
                    const pct = ((city.value / totalEventsCount) * 100).toFixed(0); 
                    return (<div key={city.name}><div className="flex justify-between mb-1"><span className="text-xs font-bold text-slate-700 dark:text-slate-200">{city.name}</span></div><div className="flex items-center gap-3"><div className="flex-1 h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden"><div className="h-full rounded-full transition-all duration-1000 ease-out bg-sky-500" style={{ width: `${pct}%` }}></div></div><span className="text-[10px] font-bold text-slate-500 dark:text-slate-400 w-6 text-right">{pct}%</span></div></div>); }) : (<p className="text-center text-slate-400 italic py-4 text-xs">{t.noLocationData}</p>)}
                </div>
              </div>
            </div>
        </div>
        <div className="mb-8"><IpTrackingCard events={baseEvents} /></div>
      </div>

      {showLinkBuilder && domain && (<div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"><div className="bg-white dark:bg-slate-900 rounded-[32px] p-6 md:p-8 max-w-md w-full shadow-2xl border border-slate-100 dark:border-slate-800"><div className="flex justify-between items-start mb-6"><h3 className="text-xl font-bold text-slate-900 dark:text-white">{t.linkBuilderTitle}</h3><button onClick={() => setShowLinkBuilder(false)}><X size={24} className="text-slate-400" /></button></div><div className="space-y-4"><div><label className="text-xs font-bold uppercase text-slate-500 dark:text-slate-400 tracking-wider">{t.sourceName}</label><input className="w-full mt-2 p-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl outline-none focus:border-indigo-500 text-slate-900 dark:text-white text-sm" placeholder="e.g. facebook" value={lbSource} onChange={(e) => setLbSource(e.target.value)} /><div className="flex flex-wrap gap-2 mt-3">{['Facebook', 'Google', 'Instagram', 'Yelp', 'TikTok'].map(s => (<button key={s} onClick={() => setLbSource(s.toLowerCase())} className="px-2.5 py-1.5 text-[10px] font-medium bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 rounded-lg hover:bg-indigo-50 hover:text-indigo-600 transition-colors">{s}</button>))}</div></div><div className="p-4 bg-slate-900 dark:bg-black rounded-xl text-indigo-200 font-mono text-[10px] md:text-xs break-all relative">{domain.url}/?s={lbSource.trim() || 'source'}<button onClick={() => { navigator.clipboard.writeText(`${domain.url}/?s=${lbSource.trim()}`); setLbCopied(true); setTimeout(() => setLbCopied(false), 2000); }} className="absolute top-2 right-2 p-1.5 bg-white/10 rounded hover:bg-white/20 text-white">{lbCopied ? <Check size={14} /> : <Copy size={14} />}</button></div></div></div></div>)}
      {showShareModal && domain && (<div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"><div className="bg-white dark:bg-slate-900 rounded-[32px] p-6 md:p-8 max-w-md w-full shadow-2xl border border-slate-100 dark:border-slate-800"><div className="flex justify-between items-start mb-6"><h3 className="text-xl font-bold text-slate-900 dark:text-white">{t.shareReportTitle}</h3><button onClick={() => setShowShareModal(false)}><X size={24} className="text-slate-400" /></button></div><div className="p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-xl text-indigo-700 dark:text-indigo-300 font-mono text-[10px] md:text-xs break-all relative">{window.location.origin}/#/public/{domain.id}<button onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/#/public/${domain.id}`); setShareCopied(true); setTimeout(() => setShareCopied(false), 2000); }} className="absolute top-2 right-2 p-1.5 bg-indigo-200 dark:bg-indigo-800 text-indigo-800 dark:text-indigo-200 rounded-lg">{shareCopied ? <Check size={14} /> : <Copy size={14} />}</button></div></div></div>)}
      {showEmbedModal && domain && <EmbedModal domain={domain} onClose={() => setShowEmbedModal(false)} />}
    </Layout>
  );
};

export default Report;